import { useState, useEffect } from 'react'
import { adminApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatDate } from '../../config'
import type { AmbassadorSubmission, AmbassadorStats } from '../../types'
import LoadingSpinner from '../../components/common/LoadingSpinner'

const AFFILIATION_LABELS: Record<string, string> = {
  'Parent/Guardian': 'Parent/Guardian',
  'Educator': 'Educator',
  'Student 14-17': 'Student (14-17)',
  'Community Member': 'Community Member',
  'Business Owner': 'Business Owner',
}

const CONTACT_METHOD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  both: 'Both',
}

export default function AdminAmbassadors() {
  const { getToken } = useAuth()
  const [ambassadors, setAmbassadors] = useState<AmbassadorSubmission[]>([])
  const [stats, setStats] = useState<AmbassadorStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedAmbassador, setSelectedAmbassador] = useState<AmbassadorSubmission | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [affiliationFilter, setAffiliationFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const fetchData = async () => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const data = await adminApi.getAmbassadors(token)
      setAmbassadors(data.ambassadors)
      setStats(data.stats)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [getToken])

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  const getAmbassadorDisplayName = (ambassador: AmbassadorSubmission) => {
    return `${ambassador.firstName} ${ambassador.lastName}`
  }

  // Get unique affiliations for filter
  const uniqueAffiliations = Array.from(
    new Set(ambassadors.map((a) => a.affiliation))
  ).sort()

  // Filter ambassadors
  const filteredAmbassadors = ambassadors.filter((a) => {
    // Status filter
    if (statusFilter !== 'all' && a.status !== statusFilter) {
      return false
    }

    // Affiliation filter
    if (affiliationFilter !== 'all' && a.affiliation !== affiliationFilter) {
      return false
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const name = getAmbassadorDisplayName(a).toLowerCase()
      const email = a.email.toLowerCase()
      const city = (a.city || '').toLowerCase()
      if (!name.includes(query) && !email.includes(query) && !city.includes(query)) {
        return false
      }
    }

    return true
  })

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Ambassadors</h1>
      </div>

      {/* KPI Summary */}
      {stats && (
        <div className="card mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div
              className="cursor-pointer p-2 rounded hover:bg-gray-50"
              onClick={() => setStatusFilter('all')}
            >
              <p className="text-2xl font-bold text-gray-800">
                {stats.totalCount}
              </p>
              <p className="text-sm text-gray-500">Total Ambassadors</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${statusFilter === 'paid' ? 'bg-green-50 ring-2 ring-green-500' : 'hover:bg-gray-50'}`}
              onClick={() => setStatusFilter(statusFilter === 'paid' ? 'all' : 'paid')}
            >
              <p className="text-2xl font-bold text-green-600">
                {stats.paidCount}
              </p>
              <p className="text-sm text-gray-500">Paid</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${statusFilter === 'pending' ? 'bg-yellow-50 ring-2 ring-yellow-500' : 'hover:bg-gray-50'}`}
              onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
            >
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pendingCount}
              </p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
            <div className="p-2">
              <p className="text-2xl font-bold text-sla-green">
                {formatCurrency(stats.paidCount * 2500)}
              </p>
              <p className="text-sm text-gray-500">Revenue</p>
            </div>
          </div>

          {(statusFilter !== 'all' || affiliationFilter !== 'all') && (
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setStatusFilter('all')
                  setAffiliationFilter('all')
                }}
                className="text-sm text-sla-blue hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <input
              type="search"
              className="input"
              placeholder="Search by name, email, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div>
            <select
              className="input"
              value={affiliationFilter}
              onChange={(e) => setAffiliationFilter(e.target.value)}
            >
              <option value="all">All Affiliations</option>
              {uniqueAffiliations.map((aff) => (
                <option key={aff} value={aff}>
                  {AFFILIATION_LABELS[aff] || aff}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Ambassadors Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Affiliation
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  T-Shirt
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Date
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredAmbassadors.map((a) => (
                <tr key={a.ambassadorId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">
                      {getAmbassadorDisplayName(a)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {a.city}, {a.zip}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-600">{a.email}</div>
                    <div className="text-sm text-gray-500">{a.phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {AFFILIATION_LABELS[a.affiliation] || a.affiliation}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium text-gray-700">
                      {a.tshirtSize}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(a.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(a.status)}`}
                    >
                      {a.status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setSelectedAmbassador(a)}
                      className="text-sla-blue hover:underline text-sm"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAmbassadors.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchQuery
                ? 'No ambassadors match your search'
                : statusFilter !== 'all' || affiliationFilter !== 'all'
                ? 'No ambassadors match the selected filters'
                : 'No ambassadors found'}
            </div>
          )}
        </div>
      </div>

      {/* Ambassador Detail Modal */}
      {selectedAmbassador && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Ambassador Details
                </h2>
                <button
                  onClick={() => setSelectedAmbassador(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex items-center gap-4">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${getStatusBadgeClass(selectedAmbassador.status)}`}
                  >
                    {selectedAmbassador.status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </div>

                {/* Contact Info */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Contact Information</h3>
                  <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                    <p><strong>Name:</strong> {getAmbassadorDisplayName(selectedAmbassador)}</p>
                    <p><strong>Email:</strong> {selectedAmbassador.email}</p>
                    <p><strong>Phone:</strong> {selectedAmbassador.phone}</p>
                    <p><strong>Preferred Contact:</strong> {CONTACT_METHOD_LABELS[selectedAmbassador.preferredContactMethod]}</p>
                    <p><strong>City:</strong> {selectedAmbassador.city}</p>
                    <p><strong>Zip:</strong> {selectedAmbassador.zip}</p>
                  </div>
                </div>

                {/* Ambassador Details */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Ambassador Details</h3>
                  <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                    <p><strong>Affiliation:</strong> {AFFILIATION_LABELS[selectedAmbassador.affiliation] || selectedAmbassador.affiliation}</p>
                    <p><strong>T-Shirt Size:</strong> {selectedAmbassador.tshirtSize}</p>
                  </div>

                  {selectedAmbassador.helpAreas && selectedAmbassador.helpAreas.length > 0 && (
                    <div className="mt-4">
                      <p className="font-medium text-gray-700 mb-2">Areas to Help:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedAmbassador.helpAreas.map((area) => (
                          <span
                            key={area}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-sla-green/10 text-sla-green border border-sla-green/20"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedAmbassador.referralSource && (
                    <div className="mt-4">
                      <p className="text-gray-600">
                        <strong>How they heard about us:</strong>{' '}
                        {selectedAmbassador.referralSource === 'Other' && selectedAmbassador.referralSourceOther
                          ? `Other - ${selectedAmbassador.referralSourceOther}`
                          : selectedAmbassador.referralSource}
                      </p>
                    </div>
                  )}
                </div>

                {/* Optional Info */}
                {(selectedAmbassador.statementOfInterest || selectedAmbassador.fundraisingExpertise) && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">Additional Information</h3>

                    {selectedAmbassador.statementOfInterest && (
                      <div className="mb-4">
                        <p className="font-medium text-gray-700 text-sm mb-1">Statement of Interest:</p>
                        <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                          {selectedAmbassador.statementOfInterest}
                        </p>
                      </div>
                    )}

                    {selectedAmbassador.fundraisingExpertise && (
                      <div>
                        <p className="font-medium text-gray-700 text-sm mb-1">Fundraising Expertise/Skills:</p>
                        <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                          {selectedAmbassador.fundraisingExpertise}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Info */}
                <div className="border rounded-lg p-4 bg-green-50">
                  <h3 className="font-semibold text-gray-700 mb-2">Payment</h3>
                  <div className="text-sm text-gray-600">
                    <p><strong>Amount:</strong> {formatCurrency(selectedAmbassador.amountDueCents)}</p>
                    {selectedAmbassador.amountPaidCents !== undefined && (
                      <p><strong>Paid:</strong> {formatCurrency(selectedAmbassador.amountPaidCents)}</p>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="text-xs text-gray-500">
                  <p>ID: {selectedAmbassador.ambassadorId}</p>
                  <p>Created: {formatDate(selectedAmbassador.createdAt)}</p>
                  <p>Updated: {formatDate(selectedAmbassador.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
